import { createPortal } from 'react-dom';
import { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';

import { useResponsive } from 'src/hooks/use-responsive';

import { Iconify } from 'src/components/iconify';

import { ToggleButton } from 'src/sections/chat/components/styles';
import { useCollapseNav } from 'src/sections/chat/hooks/use-collapse-nav';

// ----------------------------------------------------------------------

const PANEL_COLLAPSED_WIDTH = 72;

/** @type {any} */
const IconifyAny = Iconify;
/** @type {any} */
const ToggleButtonAny = ToggleButton;

// ----------------------------------------------------------------------

const MobilePanelContext = createContext(/** @type {{ onClose: () => void } | null} */ (null));

export function useMobilePanel() {
  return useContext(MobilePanelContext);
}

export function CollapsibleSidePanel({
  side = 'right',
  children,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  expandedFlex = '0 0 25%',
  expandedWidth = '25%',
  secondaryIcon = 'solar:settings-linear',
  secondaryTooltip = 'Options',
  mobileToggleIcon = 'solar:settings-linear',
  mobileToggleAriaLabel = 'Open panel',
  keepMounted = false,
}) {
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');
  const isLeft = side === 'left';
  const { openMobile, onOpenMobile, onCloseMobile } = useCollapseNav();
  const [expandedDesktopUncontrolled, setExpandedDesktopUncontrolled] = useState(defaultExpanded);

  /**
   * The panel content is rendered exactly once into this detached node and then
   * portalled in. Moving the node between the desktop and mobile hosts keeps its
   * DOM identity across breakpoint changes, which matters for content mounted
   * imperatively by third-party libraries (e.g. the Jasper input controls).
   */
  const portalNodeRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const desktopHostRef = useRef(/** @type {HTMLElement | null} */ (null));
  const mobileHostRef = useRef(/** @type {HTMLElement | null} */ (null));

  if (!portalNodeRef.current && typeof document !== 'undefined') {
    const node = document.createElement('div');
    node.style.display = 'flex';
    node.style.flexDirection = 'column';
    node.style.flex = '1 1 auto';
    node.style.minHeight = '0';
    portalNodeRef.current = node;
  }

  const syncPortalHost = useCallback(() => {
    const node = portalNodeRef.current;
    const host = mdUp ? desktopHostRef.current : mobileHostRef.current;

    if (node && host && node.parentNode !== host) {
      host.appendChild(node);
    }
  }, [mdUp]);

  const setDesktopHost = useCallback(
    (el) => {
      desktopHostRef.current = el;
      syncPortalHost();
    },
    [syncPortalHost]
  );

  const setMobileHost = useCallback(
    (el) => {
      mobileHostRef.current = el;
      syncPortalHost();
    },
    [syncPortalHost]
  );

  useEffect(() => {
    syncPortalHost();
  }, [syncPortalHost, openMobile]);

  const expandedDesktop = expandedProp ?? expandedDesktopUncontrolled;

  const setExpandedDesktop = useCallback(
    (value) => {
      const next = typeof value === 'function' ? value(expandedDesktop) : value;
      onExpandedChange?.(next);
      if (expandedProp === undefined) {
        setExpandedDesktopUncontrolled(next);
      }
    },
    [expandedDesktop, expandedProp, onExpandedChange]
  );

  const handleToggleSidebar = useCallback(() => {
    if (!mdUp) {
      onOpenMobile();
      return;
    }
    setExpandedDesktop((prev) => !prev);
  }, [mdUp, onOpenMobile, setExpandedDesktop]);

  const handleExpandPanel = useCallback(() => {
    if (!mdUp) {
      onOpenMobile();
      return;
    }
    setExpandedDesktop(true);
  }, [mdUp, onOpenMobile, setExpandedDesktop]);

  const mobilePanelContext = useMemo(
    () => (mdUp ? null : { onClose: onCloseMobile }),
    [mdUp, onCloseMobile]
  );

  const renderPanelContent =
    portalNodeRef.current &&
    createPortal(
      <MobilePanelContext.Provider value={mobilePanelContext}>
        <Stack sx={{ minHeight: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Stack>
      </MobilePanelContext.Provider>,
      portalNodeRef.current
    );

  const collapsedTooltipPlacement = isLeft ? 'right' : 'left';

  const renderDesktopPanel = (
    <Stack
      sx={{
        minHeight: '100%',
        height: '100%',
        alignSelf: 'stretch',
        flex: expandedDesktop ? expandedFlex : `0 0 ${PANEL_COLLAPSED_WIDTH}px`,
        width: expandedDesktop ? expandedWidth : PANEL_COLLAPSED_WIDTH,
        display: { xs: 'none', md: 'flex' },
        ...(isLeft
          ? { borderRight: `solid 1px ${theme.vars.palette.divider}` }
          : { borderLeft: `solid 1px ${theme.vars.palette.divider}` }),
        transition: theme.transitions.create(['width', 'flex-basis', 'transform', 'opacity'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Stack sx={{ minHeight: 0, flex: '1 1 auto' }}>
        {expandedDesktop ? (
          <Stack sx={{ p: 1.5, gap: 0.5, flexShrink: 0 }}>
            <ListItemButton onClick={handleToggleSidebar} sx={{ borderRadius: 1.5, py: 1, mb: 2 }}>
              <IconifyAny width={20} icon="cuida:sidebar-collapse-outline" />
              <Typography variant="body2" sx={{ ml: 1.5 }}>
                Collapse
              </Typography>
            </ListItemButton>
          </Stack>
        ) : (
          <Stack sx={{ alignItems: 'center', py: 1.5, gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Expand sidebar" placement={collapsedTooltipPlacement} sx={{ mb: 2 }}>
              <IconButton onClick={handleToggleSidebar}>
                <IconifyAny width={20} icon="cuida:sidebar-expand-outline" />
              </IconButton>
            </Tooltip>
            <Tooltip title={secondaryTooltip} placement={collapsedTooltipPlacement}>
              <IconButton onClick={handleExpandPanel}>
                <IconifyAny width={20} icon={secondaryIcon} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        <Box
          ref={setDesktopHost}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            display: expandedDesktop ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      <ToggleButtonAny
        onClick={onOpenMobile}
        sx={{
          display: { md: 'none' },
          ...(isLeft
            ? { left: 0, right: 'auto', borderRadius: '0 12px 12px 0' }
            : { right: 0, left: 'auto', borderRadius: '12px 0 0 12px' }),
        }}
        aria-label={mobileToggleAriaLabel}
      >
        <IconifyAny width={16} icon={mobileToggleIcon} />
      </ToggleButtonAny>

      {renderPanelContent}

      {mdUp ? renderDesktopPanel : null}

      <Drawer
        anchor={isLeft ? 'left' : 'right'}
        open={openMobile}
        onClose={onCloseMobile}
        slotProps={{ backdrop: { invisible: true } }}
        ModalProps={{ keepMounted }}
        PaperProps={{
          sx: {
            width: isLeft
              ? { xs: 'min(100vw - 16px, 320px)', sm: 'min(100vw - 32px, 360px)' }
              : { xs: 'min(100vw - 16px, 480px)', sm: 'min(100vw - 32px, 520px)' },
          },
        }}
        sx={{ display: { md: 'none' } }}
      >
        <Stack ref={setMobileHost} sx={{ minHeight: 0, flex: '1 1 auto', height: '100%' }} />
      </Drawer>
    </>
  );
}
